/**
 * PRP-MCP-10 work item 1 (G1): the affiliate program over HTTP.
 *
 * Admin routes are `/api/v1/admin/affiliates*` (admin only on the server); the
 * affiliate's own view is `/api/v1/me/referrals`. Its own module like
 * `adminUserAccessApi.ts` (`api.ts` sits at its god-file ceiling). Money is cents.
 */
import { api } from './api';

export interface Affiliate {
  id: string;
  code: string;
  name: string;
  status: 'active' | 'paused';
  user_id: string | null;
  payout_email: string | null;
  payout_method: string | null;
  created_at: string | null;
  links: string[];
  /** PRP-MCP-11 G5: the terms the affiliate accepted on the form; null for one created before, or by hand. */
  terms_version?: string | null;
  terms_accepted_at?: string | null;
  application_id?: string | null;
}

/** One submission of the anterotrail.com/affiliates form (PRP-MCP-11 G5). */
export interface AffiliateApplication {
  id: string;
  created_at: string | null;
  name: string;
  email: string;
  how: string;
  terms_version: string;
  terms_accepted_at: string;
  affiliate_id: string | null;
  status: 'new' | 'affiliate_created';
}

export interface AffiliateTotals {
  commissions_cents: number;
  paid_out_cents: number;
  owed_cents: number;
  meets_minimum: boolean;
}

export type AffiliateRow = Affiliate & AffiliateTotals & { referrals: number };

export interface AffiliateReferral {
  user_id: string;
  email: string | null;
  signed_up_at: string | null;
  account_created_at: string | null;
  spend_cents: number;
  commission_cents: number;
}

export interface AffiliateCommission {
  id: string;
  kind: 'pack' | 'trial_fee' | 'subscription' | 'refund';
  stripe_event_id: string;
  stripe_object_id: string | null;
  payment_amount_cents: number;
  commission_cents: number;
  paid_at: string | null;
  paid_out_at: string | null;
  payout_ref: string | null;
  created_at: string | null;
  referred_user_id: string;
  referred_email: string | null;
}

export interface AffiliateDetail extends AffiliateTotals {
  affiliate: Affiliate;
  referrals: AffiliateReferral[];
  commissions: AffiliateCommission[];
  minimum_payout_cents: number;
  /** Server time the detail was read; Mark paid sends it back so only these rows are stamped. */
  as_of: string;
}

export interface CreateAffiliateBody {
  name: string;
  code?: string;
  payout_email?: string;
  payout_method?: string;
  user_email?: string;
  /** Create from this application: the affiliate carries its terms version and acceptance time. */
  application_id?: string;
}

export interface MarkPaidResult {
  rows: number;
  amount_cents: number;
  payout_ref: string;
  paid_out_at: string;
}

/** One entry of the affiliate's own "Referrals" section. No referred user's name or email. */
export interface MyReferrals {
  code: string;
  status: 'active' | 'paused';
  links: string[];
  referrals: number;
  earned_cents: number;
  paid_cents: number;
  owed_cents: number;
  minimum_payout_cents: number;
  meets_minimum: boolean;
}

export const AdminAffiliatesAPI = {
  list: (): Promise<{ affiliates: AffiliateRow[] }> => api.get('/admin/affiliates'),
  create: (body: CreateAffiliateBody): Promise<Affiliate> => api.post('/admin/affiliates', body),
  newCode: (): Promise<{ code: string }> => api.get('/admin/affiliates/new-code'),
  applications: (): Promise<{ applications: AffiliateApplication[] }> => api.get('/admin/affiliate-applications'),
  get: (id: string): Promise<AffiliateDetail> => api.get(`/admin/affiliates/${encodeURIComponent(id)}`),
  setStatus: (id: string, status: 'active' | 'paused'): Promise<Affiliate> =>
    api.patch(`/admin/affiliates/${encodeURIComponent(id)}`, { status }),
  markPaid: (id: string, payoutRef: string, asOf: string): Promise<MarkPaidResult> =>
    api.post(`/admin/affiliates/${encodeURIComponent(id)}/mark-paid`, { payout_ref: payoutRef, as_of: asOf }),
};

export const MyReferralsAPI = {
  get: (): Promise<{ affiliates: MyReferrals[] }> => api.get('/me/referrals'),
};

/** `$12.34` from cents (negative keeps its sign). */
export function fmtCents(cents: number | null | undefined): string {
  const v = Number(cents ?? 0);
  const sign = v < 0 ? '-' : '';
  return `${sign}$${(Math.abs(v) / 100).toFixed(2)}`;
}

/** "Accepted terms v1 at <local time>", or "No terms acceptance on file". */
export function termsText(version: string | null | undefined, acceptedAt: string | null | undefined): string {
  if (!version || !acceptedAt) return 'No terms acceptance on file';
  return `Accepted terms v${version} at ${new Date(acceptedAt).toLocaleString()}`;
}

/** "Admins only." for a 403, else the server's detail or the fallback. */
export function affiliateErrText(err: unknown, fallback: string): string {
  const e = err as { message?: string; status?: number; response?: { status?: number; data?: { detail?: string } } };
  if (e?.status === 403 || e?.response?.status === 403) return 'Admins only.';
  return e?.response?.data?.detail || e?.message || fallback;
}
